import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ApartmentOutlined,
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
    LinkOutlined,
    TagOutlined,
    SendOutlined
} from '@ant-design/icons';
import { Badge, Button, Col, Descriptions, FloatButton, Input, Modal, Row, Space, Table, Tag, Tooltip, Popconfirm } from 'antd';
import dayjs from 'moment';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import { responseStatus } from "../../constants";
import { Links } from "../../helpers/Links";
import { menuHelper } from "../../helpers/account";
import { ChiTietBienBanBanGiaoModel, TrangThaiBienBanBanGiao } from "../../models/taisan/BienBanBanGiao";
import LoadingDetailPage from "../../modules/loading/LoadingDetailPage";
import { layChiTietBienBan, tuChoiBanGiao, xacNhanBanGiao, guiYeuCauBanGiao, thuHoiBienBan } from "../../services/bienBanBanGiao";

const { TextArea } = Input;

type LoaiTaiSan = 'chinh' | 'phu_cua_chinh' | 'phu_rieng';

const LOAI_CONFIG: Record<LoaiTaiSan, { label: string; color: string; icon: React.ReactNode }> = {
    chinh: { label: 'Tài sản chính', color: 'blue', icon: <ApartmentOutlined /> },
    phu_cua_chinh: { label: 'Phụ (có tài sản chính)', color: 'cyan', icon: <LinkOutlined /> },
    phu_rieng: { label: 'Phụ riêng lẻ', color: 'orange', icon: <TagOutlined /> },
};

function getLoai(item: any, dsTaiSan: any[]): LoaiTaiSan {
    if (!item.idTaiSanChinh) return 'chinh';
    return dsTaiSan.some(x => x.idTaiSan === item.idTaiSanChinh) ? 'phu_cua_chinh' : 'phu_rieng';
}

const getTrangThaiTag = (trangThai: number) => {
    const map: Record<number, { label: string; color: string }> = {
        [TrangThaiBienBanBanGiao.DuThao]: { label: 'Dự thảo', color: 'default' },
        [TrangThaiBienBanBanGiao.ChoXacNhan]: { label: 'Chờ xác nhận', color: 'blue' },
        [TrangThaiBienBanBanGiao.HoanThanh]: { label: 'Hoàn thành', color: 'green' },
        [TrangThaiBienBanBanGiao.ThuHoi]: { label: 'Thu hồi', color: 'orange' },
        [TrangThaiBienBanBanGiao.Huy]: { label: 'Đã huỷ', color: 'default' },
    };
    const cfg = map[trangThai];
    return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : <Tag>Không xác định</Tag>;
};

const XemBienBanBanGiao = () => {
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');

    const [isLoading, setIsLoading] = useState(false);
    const [bienBan, setBienBan] = useState<ChiTietBienBanBanGiaoModel | null>(null);
    const [showModalTuChoi, setShowModal] = useState(false);
    const [lyDoTuChoi, setLyDoTuChoi] = useState('');

    const menu:any = menuHelper.checkMultiPermission([Links.BienBanBanGiao.ChiTiet, Links.BienBanBanGiao.XacNhan]);
    const user = useSelector((state: any) => state.auth.currentUser);

    // Kiểm tra user hiện tại có trong danh sách chờ xác nhận không
    const isNguoiCanXacNhan = () =>
        !!bienBan?.danhSachXacNhan?.some(
            (x: any) => x.idNhanVien === user?.idNhanVien && x.trangThaiXacNhan === 0
        );

    const loadChiTiet = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const result = await layChiTietBienBan(parseInt(id));
            setBienBan(result);
        } catch (err: any) {
            toast.error('Lỗi khi tải chi tiết biên bản: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleXacNhan = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const res = await xacNhanBanGiao(parseInt(id));
            if (res.trangThai === responseStatus.thanhCong) {
                toast.success('Xác nhận bàn giao thành công');
                loadChiTiet();
            } else {
                toast.error(`Xác nhận không thành công: ${res.thongBao}`);
            }
        } catch (err: any) {
            toast.error('Lỗi khi xác nhận: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTuChoi = async () => {
        if (!id) return;
        if (!lyDoTuChoi.trim()) { toast.warning('Vui lòng nhập lý do từ chối'); return; }
        try {
            setIsLoading(true);
            const res = await tuChoiBanGiao(parseInt(id), lyDoTuChoi);
            if (res.trangThai === responseStatus.thanhCong) {
                toast.success('Từ chối bàn giao thành công');
                setShowModal(false);
                setLyDoTuChoi('');
                loadChiTiet();
            } else {
                toast.error(`Từ chối không thành công: ${res.thongBao}`);
            }
        } catch (err: any) {
            toast.error('Lỗi khi từ chối: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTrinh = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const res = await guiYeuCauBanGiao(parseInt(id));
            if (res.trangThai === responseStatus.thanhCong) {
                toast.success('Trình biên bản thành công');
                loadChiTiet();
            } else {
                toast.error(`Trình biên bản không thành công: ${res.thongBao}`);
            }
        } catch (err: any) {
            toast.error('Lỗi khi trình: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleThuHoi = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const res = await thuHoiBienBan(parseInt(id));
            if (res.trangThai === responseStatus.thanhCong) {
                toast.success('Thu hồi biên bản thành công');
                loadChiTiet();
            } else {
                toast.error(`Thu hồi biên bản không thành công: ${res.thongBao}`);
            }
        } catch (err: any) {
            toast.error('Lỗi khi thu hồi: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadChiTiet(); }, [id]);

    if (isLoading && !bienBan) return <LoadingDetailPage title="Chi tiết biên bản bàn giao" />;

    const dsTaiSan = bienBan?.danhSachTaiSan || [];

    const columnsTaiSan = [
        {
            title: 'STT',
            key: 'stt',
            width: 50,
            render: (_: any, __: any, idx: number) => idx + 1,
        },
        {
            title: 'Loại',
            key: 'loai',
            width: 56,
            align: 'center' as const,
            render: (_: any, row: any) => {
                const loai = getLoai(row, dsTaiSan);
                const cfg = LOAI_CONFIG[loai];
                return (
                    <Tooltip title={cfg.label}>
                        <Tag color={cfg.color} style={{ margin: 0 }}>{cfg.icon}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Mã / Tên tài sản',
            key: 'ten',
            render: (_: any, row: any) => {
                const loai = getLoai(row, dsTaiSan);
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{row.tenTaiSan}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{row.maTaiSan}</div>
                        {loai === 'phu_cua_chinh' && row.tenTaiSanChinh && (
                            <div style={{ fontSize: 11, color: '#1677ff', marginTop: 2 }}>
                                <LinkOutlined style={{ marginRight: 3 }} />
                                Phụ của: <em>{row.tenTaiSanChinh}</em>
                            </div>
                        )}
                        {loai === 'phu_rieng' && (
                            <div style={{ fontSize: 11, color: '#d46b08', marginTop: 2 }}>
                                <TagOutlined style={{ marginRight: 3 }} />Tài sản phụ riêng lẻ
                            </div>
                        )}
                    </div>
                );
            },
        },
        { title: 'SL', dataIndex: 'soLuong', key: 'sl', width: 60, align: 'center' as const },
        { title: 'Phòng ban nhận', dataIndex: 'tenDonViSuDung', key: 'donvi', width: 150 },
        {
            title: 'Người SD sau BG',
            dataIndex: 'tenNVSuDungSauBanGiao',
            key: 'nguoiSD',
            width: 160,
            render: (v: string) => v || <span style={{ color: '#bbb' }}>— (theo phòng ban)</span>,
        },
        { title: 'Tình trạng', dataIndex: 'tinhTrangTaiSan', key: 'tt', width: 110 },
        { title: 'Ghi chú', dataIndex: 'ghiChu', key: 'gc', width: 180 },
    ];

    const columnsXacNhan = [
        {
            title: 'Người xác nhận',
            key: 'nguoi',
            render: (_: any, row: any) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{row.tenNhanVien}</div>
                    {(row.chucVu || row.tenDonVi) && (
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {[row.chucVu, row.tenDonVi].filter(Boolean).join(' – ')}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'trangThaiXacNhan',
            key: 'trangThai',
            width: 140,
            render: (v: number) => {
                if (v === 0) return <Tag color="orange">Chờ xác nhận</Tag>;
                if (v === 1) return <Tag color="green">Đã đồng ý</Tag>;
                return <Tag color="red">Từ chối</Tag>;
            },
        },
        {
            title: 'Ngày xác nhận',
            dataIndex: 'ngayXacNhan',
            key: 'ngay',
            width: 150,
            render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—',
        },
        {
            title: 'Lý do từ chối',
            dataIndex: 'lyDo',
            key: 'lyDo',
            render: (v: string) => v ? <span style={{ color: 'red' }}>{v}</span> : '—',
        },
    ];

    const dsXacNhan = bienBan?.danhSachXacNhan || [];
    const daDongY = dsXacNhan.filter((x: any) => x.trangThaiXacNhan === 1).length;
    const coBiTuChoi = dsXacNhan.some((x: any) => x.trangThaiXacNhan === 2);

    return (
        <section style={{ paddingBottom: 52 }}>
            <ContentHeader title="Chi tiết biên bản bàn giao" />

            <Row>
                <Col span={24}>
                    {bienBan && (
                        <div className="card">
                            <div className="card-body">

                                <h5 className="mb-3">Thông tin biên bản</h5>
                                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                                    {/*<Descriptions.Item label="Mã biên bản">{bienBan.maBienBan}</Descriptions.Item>*/}
                                    <Descriptions.Item label="Phòng ban giao">{bienBan.tenDonViGiao}</Descriptions.Item>
                                    <Descriptions.Item label="Phòng ban nhận">{bienBan.tenDonViNhan}</Descriptions.Item>
                                    <Descriptions.Item label="Ngày lập">
                                        {dayjs(bienBan.ngayLap).format('DD/MM/YYYY')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Địa điểm">{bienBan.diaDiem || '—'}</Descriptions.Item>
                                    <Descriptions.Item label="Trạng thái">
                                        {getTrangThaiTag(bienBan.trangThai)}
                                    </Descriptions.Item>
                                    {bienBan.lyDoTuChoi && (
                                        <Descriptions.Item label="Lý do từ chối" span={2}>
                                            <span style={{ color: 'red' }}>{bienBan.lyDoTuChoi}</span>
                                        </Descriptions.Item>
                                    )}
                                    {bienBan.noiDungBanGiao && (
                                        <Descriptions.Item label="Nội dung bàn giao" span={2}>
                                            {bienBan.noiDungBanGiao}
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>

                                <h5 className="mb-3 mt-4">Người đại diện</h5>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <h6>Bên giao:</h6>
                                        <ul>
                                            {bienBan.danhSachNguoiDaiDienGiao?.map((item: any, i: number) => (
                                                <li key={i}>
                                                    {item.tenNhanVien}{item.chucVu ? ` – ${item.chucVu}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <h6>Bên nhận:</h6>
                                        <ul>
                                            {bienBan.danhSachNguoiDaiDienNhan?.map((item: any, i: number) => (
                                                <li key={i}>
                                                    {item.tenNhanVien}{item.chucVu ? ` – ${item.chucVu}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </Col>
                                </Row>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24, marginBottom: 8 }}>
                                    <h5 style={{ margin: 0 }}>Danh sách tài sản bàn giao</h5>
                                    <Badge count={dsTaiSan.length} style={{ backgroundColor: '#1677ff' }} />
                                </div>

                                <div style={{
                                    display: 'flex', gap: 16, flexWrap: 'wrap',
                                    marginBottom: 8, padding: '4px 12px',
                                    background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
                                }}>
                                    {(Object.keys(LOAI_CONFIG) as LoaiTaiSan[]).map(loai => (
                                        <Space key={loai} size={4} style={{ fontSize: 12 }}>
                                            <Tag color={LOAI_CONFIG[loai].color} style={{ margin: 0 }}>
                                                {LOAI_CONFIG[loai].icon}
                                            </Tag>
                                            <span>{LOAI_CONFIG[loai].label}</span>
                                        </Space>
                                    ))}
                                </div>

                                <Table
                                    columns={columnsTaiSan}
                                    dataSource={dsTaiSan}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 900 }}
                                />
                                {dsXacNhan.length > 0 && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, marginBottom: 8 }}>
                                            <h5 style={{ margin: 0 }}>Trạng thái xác nhận</h5>
                                            <Space size={4}>
                                                <Tag color="success">{daDongY}/{dsXacNhan.length} đã đồng ý</Tag>
                                                {coBiTuChoi && <Tag color="error">Có người từ chối</Tag>}
                                                {!coBiTuChoi && daDongY < dsXacNhan.length && (
                                                    <Tag color="processing">
                                                        Đang chờ {dsXacNhan.length - daDongY} người
                                                    </Tag>
                                                )}
                                            </Space>
                                        </div>
                                        <Table
                                            columns={columnsXacNhan}
                                            dataSource={dsXacNhan}
                                            rowKey="idNhanVien"
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: 600 }}
                                        />
                                    </>
                                )}

                                <div className="mt-3 d-flex justify-content-end">
                                    <Space>
                                        {(bienBan.trangThai === TrangThaiBienBanBanGiao.DuThao || bienBan.trangThai === TrangThaiBienBanBanGiao.ThuHoi) && menu[Links.BienBanBanGiao.ChiTiet] && (
                                            <Popconfirm title="Trình biên bản bàn giao" description="Bạn có chắc chắn muốn trình biên bản bàn giao này?" onConfirm={handleTrinh} okText="Đồng ý" cancelText="Hủy">
                                                <Button type="primary" icon={<SendOutlined />} loading={isLoading}>
                                                    Trình biên bản
                                                </Button>
                                            </Popconfirm>
                                        )}

                                        {bienBan.trangThai === TrangThaiBienBanBanGiao.ChoXacNhan && menu[Links.BienBanBanGiao.ChiTiet] && (
                                            <Popconfirm title="Thu hồi biên bản" description="Bạn có chắc chắn muốn thu hồi biên bản bàn giao này?" onConfirm={handleThuHoi} okText="Đồng ý" cancelText="Hủy">
                                                <Button style={{ color: '#faad14', borderColor: '#faad14' }} loading={isLoading}>
                                                    Thu hồi
                                                </Button>
                                            </Popconfirm>
                                        )}

                                        {bienBan.trangThai === TrangThaiBienBanBanGiao.ChoXacNhan && isNguoiCanXacNhan() && menu[Links.BienBanBanGiao.XacNhan] && (
                                            <>
                                                <Button
                                                    type="primary"
                                                    icon={<CheckOutlined />}
                                                    onClick={handleXacNhan}
                                                    loading={isLoading}
                                                    style={{ background: '#52c41a' }}
                                                >
                                                    Xác nhận bàn giao
                                                </Button>
                                                <Button
                                                    danger
                                                    icon={<CloseOutlined />}
                                                    onClick={() => setShowModal(true)}
                                                >
                                                    Từ chối
                                                </Button>
                                            </>
                                        )}
                                    </Space>
                                </div>

                            </div>
                        </div>
                    )}
                </Col>
            </Row>

            <Row className="mt-1 mb-1">
                <Col span={24}>
                    {menuHelper.checkMultiPermission([Links.BienBanBanGiao.ChiTiet]) && (bienBan?.trangThai === TrangThaiBienBanBanGiao.DuThao || bienBan?.trangThai === TrangThaiBienBanBanGiao.ThuHoi) && (
                        <FloatButton.Group trigger="hover" type="primary" style={{ right: 50, bottom: 80 }}>
                            <FloatButton
                                tooltip="Chỉnh sửa"
                                className="hrm-btn-warning"
                                icon={<EditOutlined />}
                                onClick={() => navigate(`${Links.BienBanBanGiao.ChiTiet}?id=${id}`)}
                            />
                        </FloatButton.Group>
                    )}
                </Col>
            </Row>
            <Modal
                title="Từ chối bàn giao"
                open={showModalTuChoi}
                onOk={handleTuChoi}
                onCancel={() => { setShowModal(false); setLyDoTuChoi(''); }}
                okText="Xác nhận từ chối"
                cancelText="Huỷ"
                confirmLoading={isLoading}
                okButtonProps={{ danger: true }}
            >
                <div style={{ marginTop: 16 }}>
                    <label>Lý do từ chối: <span style={{ color: 'red' }}>*</span></label>
                    <TextArea
                        rows={4}
                        value={lyDoTuChoi}
                        onChange={e => setLyDoTuChoi(e.target.value)}
                        placeholder="Nhập lý do từ chối..."
                        maxLength={500}
                        showCount
                        style={{ marginTop: 4 }}
                    />
                </div>
            </Modal>
        </section>
    );
};

export default XemBienBanBanGiao;